export type OngoingVendorReviewPeriodicAnnexureId = 'a6' | 'a7' | 'a8' | 'a10' | 'a12' | 'a13';

export type OngoingVendorReviewFieldMode =
  | 'business-owner'
  | 'answer'
  | 'justification-rating'
  | 'comments'
  | 'observation-risk'
  | 'owner-response';

export type OngoingVendorReviewAnnexureQuestion = {
  id: string;
  label: string;
  hasInfoIcon?: boolean;
  hasUpload?: boolean;
  fullWidth?: boolean;
};

export type OngoingVendorReviewAnnexureSection = {
  id: string;
  title: string;
  fieldMode: OngoingVendorReviewFieldMode;
  questions: OngoingVendorReviewAnnexureQuestion[];
};

export type OngoingVendorReviewApproverRow = {
  id: string;
  label: string;
};

export type OngoingVendorReviewAnnexureFormDefinition = {
  annexureId: OngoingVendorReviewPeriodicAnnexureId;
  title: string;
  sections: OngoingVendorReviewAnnexureSection[];
  ratingTableTitle?: string;
  showSupportingDocument?: boolean;
  showApprovers?: boolean;
  addApproversLabel?: string;
  approverRows?: OngoingVendorReviewApproverRow[];
  showNeedMoreInformation?: boolean;
  showSendForInformation?: boolean;
  showFooterActions?: boolean;
};

export const A6_SECTIONS: OngoingVendorReviewAnnexureSection[] = [
  {
    id: 'a6-general-information',
    title: 'General Information',
    fieldMode: 'business-owner',
    questions: [
      { id: 'a6-1', label: 'Business Registration', hasInfoIcon: true, hasUpload: true },
      { id: 'a6-2', label: 'Business License/Regulations (If Applicable)', hasInfoIcon: true },
      { id: 'a6-3', label: 'Comprehensive List Of All Services', hasInfoIcon: true, hasUpload: true },
      { id: 'a6-4', label: 'Locations', hasInfoIcon: true },
      { id: 'a6-5', label: 'Information On Executives', hasInfoIcon: true },
      { id: 'a6-6', label: 'Ownership Structure', hasInfoIcon: true, hasUpload: true },
      { id: 'a6-7', label: 'Principal Owners/UBOs', hasInfoIcon: true },
      { id: 'a6-8', label: 'List Of Clients', hasInfoIcon: true, hasUpload: true },
    ],
  },
  {
    id: 'a6-financial-capacity',
    title: 'Financial & Capacity Review',
    fieldMode: 'answer',
    questions: [
      { id: 'a6-9', label: 'Financial Statements/Audit Report', hasInfoIcon: true, hasUpload: true },
      { id: 'a6-10', label: 'Business Continuity Plan', hasInfoIcon: true },
      { id: 'a6-11', label: 'Disaster Recovery Plan', hasInfoIcon: true },
      { id: 'a6-12', label: 'Capacity Review', hasInfoIcon: true },
    ],
  },
  {
    id: 'a6-risk-assessment',
    title: 'Risk Assessment',
    fieldMode: 'answer',
    questions: [
      { id: 'a6-13', label: 'Negative News Report (Complaints & Negative Reviews)', hasInfoIcon: true, hasUpload: true },
      { id: 'a6-14', label: 'World Check & Sanction Screening', hasInfoIcon: true, hasUpload: true },
      { id: 'a6-15', label: 'Operational Risk', hasInfoIcon: true },
      { id: 'a6-16', label: 'Legal Risk', hasInfoIcon: true },
      { id: 'a6-17', label: 'Anti Slavery Policy', hasInfoIcon: true },
      { id: 'a6-18', label: 'Cyber Security Risk', hasInfoIcon: true, hasUpload: true },
      { id: 'a6-19', label: 'Data Protection', hasInfoIcon: true, fullWidth: true },
    ],
  },
  {
    id: 'a6-insurance',
    title: 'Insurance',
    fieldMode: 'answer',
    questions: [
      { id: 'a6-20', label: 'General Liability', hasInfoIcon: true },
      { id: 'a6-21', label: 'Insurance Specific To Services', hasInfoIcon: true },
    ],
  },
];

export const A7_SECTIONS: OngoingVendorReviewAnnexureSection[] = [
  {
    id: 'a7-financial',
    title: 'Financial Risk',
    fieldMode: 'justification-rating',
    questions: [
      {
        id: 'a7-1',
        label:
          'What Is The Potential For This Service’s Failure To Trigger Or Amplify Risks That Could Undermine Confidence In The UK Financial System?',
      },
      { id: 'a7-2', label: 'What Is The Potential Financial Impact Of A Failure Or Disruption Of This Function On PNBIL?' },
    ],
  },
  {
    id: 'a7-operational',
    title: 'Operational Risk',
    fieldMode: 'justification-rating',
    questions: [
      { id: 'a7-3', label: 'How Dependent Are Important Business Services On This Vendor?' },
      {
        id: 'a7-4',
        label: 'What Is The Impact If The Vendor Fails To Manage Concentration Risk And Key Dependencies?',
      },
      {
        id: 'a7-5',
        label:
          "Could Weaknesses In The Vendor's Continuity Or Contingency Planning Cause A Disruption To An Important Business Service?",
      },
      {
        id: 'a7-6',
        label:
          'If There Is An Impact On IBS, How Quickly Can The Bank Switch To An Alternative Solution In Case Of Failure?',
      },
    ],
  },
  {
    id: 'a7-reputational',
    title: 'Reputational Risk',
    fieldMode: 'justification-rating',
    questions: [
      {
        id: 'a7-7',
        label:
          "Could A Failure Or Disruption Of This Function Damage The Firm's Reputation And Heavily Expose To Public And Media Scrutiny?",
      },
    ],
  },
  {
    id: 'a7-compliance',
    title: 'Compliance Risk',
    fieldMode: 'justification-rating',
    questions: [
      {
        id: 'a7-8',
        label:
          'What Would Be The Potential Impact On The Bank If The Vendor Fails To Comply With Applicable Laws, Regulations, Or Regulatory Expectations (E.G., FCA, PRA, SMCR, PSD2, AML, Or Other Sector-Specific Rules)?',
      },
    ],
  },
  {
    id: 'a7-it-security',
    title: 'IT Security Risk',
    fieldMode: 'justification-rating',
    questions: [
      {
        id: 'a7-9',
        label:
          "If The IT Service Provider Experiences A Disruption, What Would Be The Impact On The Bank's Ability To Maintain Critical Operations And Services?",
      },
      {
        id: 'a7-10',
        label:
          'What Would Be The Impact If The Vendor Fails To Maintain Adequate Cybersecurity Controls, Leading To A Vulnerability Or Attack?',
      },
    ],
  },
  {
    id: 'a7-data',
    title: 'Data Risk',
    fieldMode: 'justification-rating',
    questions: [
      {
        id: 'a7-11',
        label:
          'What Would Be The Potential Impact On The Bank If The Vendor Fails To Handle Customer Data Securely Or In Compliance With Applicable Data Protection Regulations, Including But Not Limited To GDPR And Financial Sector-Specific Requirements?',
      },
    ],
  },
];

export const A8_SECTIONS: OngoingVendorReviewAnnexureSection[] = [
  {
    id: 'a8-operational-risk',
    title: 'Operational Risk',
    fieldMode: 'justification-rating',
    questions: [
      { id: 'a8-1', label: 'BCP Plan In Place' },
      {
        id: 'a8-2',
        label: 'Commitment And Ability To Comply With Contractual Terms / Agreed SLA',
      },
      {
        id: 'a8-3',
        label: 'Availability Of Independent Testing And Audits (Onsite / Offsite Audit)',
      },
    ],
  },
  {
    id: 'a8-financial-risk',
    title: 'Financial Risk',
    fieldMode: 'justification-rating',
    questions: [
      { id: 'a8-4', label: 'Any Potential/Impending Insolvency' },
      { id: 'a8-5', label: 'Any Adverse Audit Reports' },
      { id: 'a8-6', label: 'Huge/Continuing Losses' },
    ],
  },
  {
    id: 'a8-climate-risk',
    title: 'Climate Risk',
    fieldMode: 'justification-rating',
    questions: [{ id: 'a8-7', label: 'Assessment of Environmental, Social & Climate Change' }],
  },
  {
    id: 'a8-governance-risk',
    title: 'Governance Risk',
    fieldMode: 'justification-rating',
    questions: [
      {
        id: 'a8-8',
        label: 'Unclear/ Over-Complex Shareholding Structure/Governance Structure',
      },
      { id: 'a8-9', label: 'Clearly Defined Policies & Procedures In Place' },
      { id: 'a8-10', label: 'Fraud Management Controls In Place' },
    ],
  },
  {
    id: 'a8-data-risk',
    title: 'Data Risk',
    fieldMode: 'justification-rating',
    questions: [
      { id: 'a8-11', label: 'Data Quality Management' },
      { id: 'a8-12', label: 'Any Data Breach incidents in the Past 12 Months' },
    ],
  },
  {
    id: 'a8-hr-risk',
    title: 'HR Risk',
    fieldMode: 'justification-rating',
    questions: [{ id: 'a8-13', label: 'Robust information security management system' }],
  },
  {
    id: 'a8-it-cyber-security',
    title: 'IT Cyber Security Risk',
    fieldMode: 'justification-rating',
    questions: [{ id: 'a8-14', label: 'Approved by CISO' }],
  },
  {
    id: 'a8-concentration-risk',
    title: 'Concentration Risk',
    fieldMode: 'justification-rating',
    questions: [
      {
        id: 'a8-15',
        label: 'Whether the said vendor is already providing some other services to bank',
      },
    ],
  },
  {
    id: 'a8-sub-contracting-risk',
    title: 'Sub-contracting Risk',
    fieldMode: 'justification-rating',
    questions: [{ id: 'a8-16', label: 'The extent of sub-contractors used by our service provider' }],
  },
];

export const A10_SECTIONS: OngoingVendorReviewAnnexureSection[] = [
  {
    id: 'a10-operational',
    title: 'Operational',
    fieldMode: 'comments',
    questions: [
      { id: 'a10-1', label: 'Detail Of Outsourced Activities', hasInfoIcon: true },
      { id: 'a10-2', label: 'Provided Directly By 3rd Party/Directly By PNB India', hasInfoIcon: true },
      {
        id: 'a10-3',
        label: 'Does The Vendor Supports IBS (Cash Withdrawal, Domestic & Foreign Payment Services)',
        hasInfoIcon: true,
      },
      { id: 'a10-4', label: 'Services Provided To Corporate Office / Branch / Back Office', hasInfoIcon: true },
      { id: 'a10-5', label: 'Frequency Of Vendor Review Meetings', hasInfoIcon: true },
      { id: 'a10-6', label: 'Updated Contact Details Available For The Vendor', hasInfoIcon: true },
      { id: 'a10-7', label: 'Overall Performance Of The Service Provider', hasInfoIcon: true },
      { id: 'a10-8', label: 'Any Reported Breaches / Service Issues As Per The SLA', hasInfoIcon: true },
      { id: 'a10-9', label: 'New Automation Prospective', hasInfoIcon: true },
      { id: 'a10-10', label: 'Is Subcontracting/Third Party Outsourcing Permitted', hasInfoIcon: true },
    ],
  },
  {
    id: 'a10-legal',
    title: 'Legal',
    fieldMode: 'comments',
    questions: [
      { id: 'a10-11', label: 'Date Contract Signed', hasInfoIcon: true },
      { id: 'a10-12', label: 'Renewal Date Of Contract', hasInfoIcon: true },
      { id: 'a10-13', label: 'Last Legal Review Date', hasInfoIcon: true },
      { id: 'a10-14', label: 'SLA In Place', hasInfoIcon: true },
      { id: 'a10-15', label: 'Whether Latest Insurance Policy Available', hasInfoIcon: true },
      { id: 'a10-16', label: 'Conflicts Of Interest Identified', hasInfoIcon: true },
      {
        id: 'a10-17',
        label: 'Any Recent Changes In The Company i.e Financial Position, Ownership, Sub-Outsourcing',
        hasInfoIcon: true,
      },
    ],
  },
  {
    id: 'a10-compliance',
    title: 'Compliance',
    fieldMode: 'comments',
    questions: [
      { id: 'a10-18', label: 'KYC / AML Due Diligence Last Completed Date As Per Annexure 6', hasInfoIcon: true },
      { id: 'a10-19', label: 'Complies With GDPR', hasInfoIcon: true },
      { id: 'a10-20', label: 'Complies With Modern Slavery Act Updated On Annual Basis', hasInfoIcon: true },
      { id: 'a10-21', label: 'Complies With Anti-Bribery', hasInfoIcon: true },
      { id: 'a10-22', label: 'Under New Consumer Duty, Risk Of Consumer Harm', hasInfoIcon: true },
      { id: 'a10-23', label: 'Any Adverse News About Vendor / Red Flags / Adverse Media Screening', hasInfoIcon: true },
    ],
  },
  {
    id: 'a10-it-security',
    title: 'IT & Security',
    fieldMode: 'comments',
    questions: [
      { id: 'a10-24', label: 'Cybersecurity Arrangements', hasInfoIcon: true },
      { id: 'a10-25', label: 'Does The Supplier Allow The Use Of Portable Media/Devices', hasInfoIcon: true },
    ],
  },
  {
    id: 'a10-risk-management',
    title: 'Risk Management',
    fieldMode: 'comments',
    questions: [
      { id: 'a10-26', label: 'Materiality Assessment As Per Annexure 7', hasInfoIcon: true },
      { id: 'a10-27', label: 'BCP/DR Plans Provided / Last Test Date', hasInfoIcon: true },
      {
        id: 'a10-28',
        label: 'Risk Assessment Date And Risk Rating As Per Annexure 8',
        hasInfoIcon: true,
        fullWidth: true,
      },
    ],
  },
];

export const A12_SECTIONS: OngoingVendorReviewAnnexureSection[] = [
  {
    id: 'a12-vendor-visit-checklist',
    title: 'Checklist Point',
    fieldMode: 'observation-risk',
    questions: [
      { id: 'a12-1', label: 'Physical And Cyber Security Control' },
      { id: 'a12-2', label: 'Evaluation Of Health And Safety Policy And Procedures' },
      { id: 'a12-3', label: 'ISO Certification Visible In The Building' },
      { id: 'a12-4', label: 'Interaction With Employee For Operational Activity' },
      { id: 'a12-5', label: 'Over All Working Atmosphere In The Vendor Location' },
      { id: 'a12-6', label: 'Overall Comments' },
    ],
  },
];

const A13_SECTIONS: OngoingVendorReviewAnnexureSection[] = [
  {
    id: 'a13-bcp-dr-assessment',
    title: 'Department RM Response',
    fieldMode: 'owner-response',
    questions: [
      {
        id: 'a13-1',
        label: 'Business Continuity Standard For The Full Range Of Products, Services And Works You Provide?',
      },
      {
        id: 'a13-2',
        label: 'Have You Assessed The Risks To Your Operations And Taken The Necessary Actions To Protect?',
      },
      {
        id: 'a13-3',
        label:
          'Do You Identify And Mitigate Risks Or Threats To The Business Operations From Specific Events Such As Warnings Of Heavy Weather, System Failure, And Power Outages?',
      },
      {
        id: 'a13-4',
        label:
          'Do You Have A Business Continuity Strategy For People, Premises, Technology, Information, Suppliers And Stakeholders?',
      },
      {
        id: 'a13-5',
        label:
          'Have You Regularly Updated Business Continuity Arrangement That Includes Your Incident Management Process, Notification Procedures, Recovery Strategy/Procedures And The Estimated Recovery Time?',
      },
      {
        id: 'a13-6',
        label: 'Can You Provide Information On Your BCP Exercising Program And Evidence Of Your Most Recent Exercises?',
      },
      {
        id: 'a13-7',
        label: 'Are All Staff Aware Of The BC Procedures And Their Roles And Responsibilities Within Them?',
      },
      { id: 'a13-8', label: 'Do You Have A Proven And Effective IT Continuity Plan? Please Provide Evidence' },
      { id: 'a13-9', label: 'Is All Critical Data Backed Up And Readily Available Offsite?', fullWidth: true },
    ],
  },
];

const DEFAULT_APPROVER_ROWS: OngoingVendorReviewApproverRow[] = [
  { id: 'a1', label: 'John Doe' },
  { id: 'a2', label: 'Alice Smith' },
];

export const ONGOING_VENDOR_REVIEW_ANNEXURE_FORMS: Record<
  OngoingVendorReviewPeriodicAnnexureId,
  OngoingVendorReviewAnnexureFormDefinition
> = {
  a6: {
    annexureId: 'a6',
    title: 'Annexure 6 – KYC Due Diligence Form',
    sections: A6_SECTIONS,
    showSupportingDocument: true,
    showApprovers: true,
    addApproversLabel: '+ Add Approvers Max 5',
    approverRows: DEFAULT_APPROVER_ROWS,
    showFooterActions: true,
  },
  a7: {
    annexureId: 'a7',
    title: 'Annexure 7 – Materiality Assessment Form',
    sections: A7_SECTIONS,
    ratingTableTitle: 'Rating Table',
    showSupportingDocument: true,
    showApprovers: true,
    addApproversLabel: '+ Add Approvers Max 5',
    approverRows: DEFAULT_APPROVER_ROWS,
    showNeedMoreInformation: true,
    showFooterActions: true,
  },
  a8: {
    annexureId: 'a8',
    title: 'Annexure 8 – Risk Assessment Form',
    sections: A8_SECTIONS,
    ratingTableTitle: 'Rating Table',
    showSupportingDocument: true,
    showApprovers: true,
    addApproversLabel: '+ Add Approvers Max 5',
    approverRows: DEFAULT_APPROVER_ROWS,
    showSendForInformation: true,
    showFooterActions: true,
  },
  a10: {
    annexureId: 'a10',
    title: 'Annexure 10 – Service Provider Periodic Assessment',
    sections: A10_SECTIONS,
    showSupportingDocument: true,
    showApprovers: true,
    addApproversLabel: '+ Add Approvers Max 5',
    approverRows: DEFAULT_APPROVER_ROWS,
    showFooterActions: true,
  },
  a12: {
    annexureId: 'a12',
    title: 'Annexure 12 – Vendor Visit format',
    sections: A12_SECTIONS,
    showSupportingDocument: true,
    showApprovers: true,
    addApproversLabel: '+ Add Approvers Max 5',
    approverRows: DEFAULT_APPROVER_ROWS,
    showFooterActions: true,
  },
  a13: {
    annexureId: 'a13',
    title: 'Annexure 13 – Business Continuity Plan and Disaster Recovery Assessment',
    sections: A13_SECTIONS,
    showSupportingDocument: true,
    showApprovers: true,
    addApproversLabel: '+ Add Approvers Max 5',
    approverRows: DEFAULT_APPROVER_ROWS,
    showFooterActions: true,
  },
};
